import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { secureStorage } from '../utils/secureStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(secureStorage.getToken());
  const [loading, setLoading] = useState(true);

  // Set axios default header and start heartbeat
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      
      // Start heartbeat to track user activity (every 30 seconds for live tracking)
      const heartbeatInterval = setInterval(() => {
        sendHeartbeat();
      }, 30000); // 30 seconds for more responsive live tracking
      
      // Initial heartbeat
      sendHeartbeat();
      
      return () => clearInterval(heartbeatInterval);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Send heartbeat with cart and location data - exposed for cart updates
  const sendHeartbeat = async (cartData = null) => {
    if (!token) return;
    
    try {
      // Get geolocation from localStorage
      const geoData = localStorage.getItem('user_geo');
      let latitude, longitude;
      if (geoData) {
        try {
          const geo = JSON.parse(geoData);
          latitude = geo.lat;
          longitude = geo.lng;
        } catch (e) {}
      }
      
      // Build heartbeat payload
      const payload = {
        latitude,
        longitude,
        current_page: window.location.pathname,
        ...cartData
      };
      
      await axios.post(`${API}/auth/heartbeat`, payload);
    } catch (error) {
      // Silent fail for heartbeat
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const freshUser = response.data;
      
      // Update user state with fresh data from server
      setUser(freshUser);
      secureStorage.setUser(freshUser);
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout if token is explicitly invalid (401), not for network errors
      if (error.response?.status === 401) {
        logout();
      } else {
        // For network errors, try to restore from cache
        const cachedUser = secureStorage.getUser();
        const currentToken = secureStorage.getToken();
        
        if (cachedUser && currentToken) {
          if (cachedUser.customer_id && cachedUser.mobile && cachedUser.name) {
            setUser(cachedUser);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData, {
        timeout: 30000  // 30 second timeout
      });
      const { requires_approval } = response.data;
      
      if (requires_approval) {
        toast.success('Registration successful! Awaiting admin approval.');
        return { success: true, requires_approval: true };
      }
      
      toast.success('Registration successful!');
      return { success: true, requires_approval: false };
    } catch (error) {
      console.error('Registration error:', error);
      let message = 'Registration failed. Please try again.';
      
      if (error.code === 'ECONNABORTED') {
        message = 'Request timed out. Please check your internet connection.';
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const login = async (mobile, otp) => {
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { mobile, otp });
      const { token: newToken, customer } = response.data;
      
      // CRITICAL: Clear ALL previous user data first to prevent data leakage
      secureStorage.clearAll();
      Object.keys(localStorage).filter(k => k.startsWith('img_') || k.startsWith('cached_')).forEach(k => localStorage.removeItem(k));
      
      // Validate that the returned customer matches the login mobile
      if (customer.mobile !== mobile) {
        console.error('Security: Customer mobile mismatch!', { expected: mobile, got: customer.mobile });
        toast.error('Login error - please try again');
        return { success: false, error: 'Data mismatch' };
      }
      
      setToken(newToken);
      setUser(customer);
      secureStorage.setToken(newToken);
      secureStorage.setUser(customer);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      
      // Check if this is a pending approval error
      if (message.includes('APPROVAL_PENDING')) {
        return { success: false, pending_approval: true };
      }
      
      toast.error(message.replace('APPROVAL_PENDING|', ''));
      return { success: false, error: message };
    }
  };

  const sendOTP = async (mobile) => {
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { mobile });
      toast.success(response.data.message);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send OTP';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyMsg91Token = async (mobile, msg91Token) => {
    try {
      const response = await axios.post(`${API}/auth/verify-msg91`, { mobile, msg91_token: msg91Token });
      const { token: newToken, customer } = response.data;
      
      // CRITICAL: Clear ALL previous user data first
      secureStorage.clearAll();
      Object.keys(localStorage).filter(k => k.startsWith('img_') || k.startsWith('cached_')).forEach(k => localStorage.removeItem(k));
      
      // Validate match
      if (customer.mobile !== mobile) {
        console.error('Security: Customer mobile mismatch!', { expected: mobile, got: customer.mobile });
        return { success: false, error: 'Data mismatch' };
      }
      
      setToken(newToken);
      setUser(customer);
      secureStorage.setToken(newToken);
      secureStorage.setUser(customer);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      
      // Check if this is a pending approval error
      if (message.includes('APPROVAL_PENDING')) {
        return { success: false, pending_approval: true };
      }
      
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    secureStorage.clearAll();
    // Clear all cached data including images
    Object.keys(localStorage).filter(k => k.startsWith('img_') || k.startsWith('cached_')).forEach(k => localStorage.removeItem(k));
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const requestDiscount = async (discountPercentage) => {
    try {
      const response = await axios.post(`${API}/discount/request`, {
        requested_discount: discountPercentage
      });
      
      toast.success(response.data.message);
      
      // Refresh user data
      await fetchUser();
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to request discount';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Update user data (for profile completion)
  const updateUser = (updatedCustomer) => {
    setUser(updatedCustomer);
    secureStorage.setUser(updatedCustomer);
  };

  // ─── Impersonation (master admin only — see ImpersonateCustomerSwitcher) ──
  // Master admin (9999313175) can browse the app as any customer. We stash
  // the admin's original token/user under "_orig_" keys, swap the active
  // token to the customer's freshly-minted JWT, and reload. The exit path
  // (`stopImpersonation`) restores the originals — no server call required
  // because the customer JWT is short-lived (4h) and isolated.
  const startImpersonation = async (customerId, opts = {}) => {
    const { landingPath = '/' } = opts || {};
    try {
      const res = await axios.post(`${API}/admin/impersonate/${customerId}`);
      const { token: customerToken, user: customerUser, live_cart: liveCart } = res.data;
      // Stash originals so the back button can restore them.
      try {
        sessionStorage.setItem('imp_orig_token', secureStorage.getToken() || '');
        const origUser = secureStorage.getUser();
        if (origUser) sessionStorage.setItem('imp_orig_user', JSON.stringify(origUser));
        sessionStorage.setItem('imp_target', JSON.stringify(customerUser));
        // 2026-06-05 — Stash admin's own cart so exit restores it.
        // Then seed localStorage with the customer's live cart snapshot
        // (heartbeat'd from their device into `user_sessions`). The
        // CartProvider reads `sparePartsCart` on mount, so after the
        // hard reload below, admin literally sees the customer's cart.
        try {
          const adminCart = localStorage.getItem('sparePartsCart') || '[]';
          sessionStorage.setItem('imp_orig_cart', adminCart);
        } catch (_) { /* ignore */ }
        try {
          if (Array.isArray(liveCart)) {
            localStorage.setItem('sparePartsCart', JSON.stringify(liveCart));
          } else {
            // No live snapshot — start empty rather than show admin's cart.
            localStorage.setItem('sparePartsCart', '[]');
          }
        } catch (_) { /* ignore */ }
      } catch (_) { /* ignore */ }
      // Swap in the customer's token + user, then hard reload so every
      // component re-reads auth state with the new identity.
      secureStorage.setToken(customerToken);
      secureStorage.setUser(customerUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${customerToken}`;
      window.location.href = landingPath;
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not impersonate this customer';
      toast.error(msg);
      return { success: false, error: msg };
    }
  };

  const stopImpersonation = () => {
    try {
      const origToken = sessionStorage.getItem('imp_orig_token');
      const origUserStr = sessionStorage.getItem('imp_orig_user');
      const origCart = sessionStorage.getItem('imp_orig_cart');
      sessionStorage.removeItem('imp_orig_token');
      sessionStorage.removeItem('imp_orig_user');
      sessionStorage.removeItem('imp_target');
      sessionStorage.removeItem('imp_orig_cart');
      if (origToken) {
        secureStorage.setToken(origToken);
        if (origUserStr) {
          try { secureStorage.setUser(JSON.parse(origUserStr)); } catch (_) {}
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${origToken}`;
      }
      // Restore admin's own cart from before the impersonation.
      try {
        if (origCart) {
          localStorage.setItem('sparePartsCart', origCart);
        }
      } catch (_) { /* ignore */ }
    } catch (_) { /* ignore */ }
    window.location.href = '/';
  };

  const isImpersonating = () => {
    try { return !!sessionStorage.getItem('imp_orig_token'); } catch { return false; }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    register,
    login,
    sendOTP,
    verifyMsg91Token,
    logout,
    requestDiscount,
    refreshUser: fetchUser,
    updateUser,
    sendHeartbeat,  // Expose for cart updates
    startImpersonation,
    stopImpersonation,
    isImpersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
